import React, { useState, useEffect } from "react";
import * as config from "../../config";

interface LogMessage {
  env: string;
  level: string;
  message: string;
  service: string;
  timestamp: string;
}

interface LogsProps {
  jwtToken: string | null;
}

const Logs: React.FC<LogsProps> = ({ jwtToken }) => {
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortKey, setSortKey] = useState<keyof LogMessage>("timestamp");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentEnv, setCurrentEnv] = useState<string>("tyau_sydney");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [logsPerPage] = useState<number>(100);

  useEffect(() => {
    let latestTimestamp = "";
    let logsenv=localStorage.getItem("selectedEnv")||"tyau_sydney";
    const fetchLogs = async () => {
      try {
        const response = await fetch(
          config.rootUrl +
            `/v1/simulator/api/logs?since=${encodeURIComponent(
              latestTimestamp,
            )}`,
          { headers: { "x-api-key": config.apiKey, "x-environment":logsenv, Authorization: "Bearer " + jwtToken  } },
        );
        if (!response.ok) {
          throw new Error("Failed to fetch log messages");
        }
        const newLogMessages: LogMessage[] = await response.json();

        if (newLogMessages.length > 0) {
          latestTimestamp = newLogMessages[newLogMessages.length - 1].timestamp;
          setLogs((prevLogs) => {
            const combinedLogs = [...prevLogs, ...newLogMessages];
            const uniqueLogs = combinedLogs.filter(
              (log, index, self) =>
                index ===
                self.findIndex(
                  (t) =>
                    t.timestamp === log.timestamp && t.message === log.message,
                ),
            );
            return uniqueLogs.sort(
              (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime(),
            );
          });
          setError(null);
        }
      } catch (error) {
        console.error("Error fetching log messages:", error);
        setError("Error fetching log messages. Please try again later.");
      }
    };

    const intervalId = setInterval(fetchLogs, 5000);
    fetchLogs();

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const sortedLogs = [...logs].sort((a, b) => {
      if (a[sortKey] < b[sortKey]) {
        return sortOrder === "asc" ? -1 : 1;
      }
      if (a[sortKey] > b[sortKey]) {
        return sortOrder === "asc" ? 1 : -1;
      }
      return 0;
    });
    setFilteredLogs(sortedLogs);
  }, [logs, sortKey, sortOrder]);

  useEffect(() => {
    const filtered = logs.filter((log) =>
      currentEnv === "tyau_sydney" ? true : log.env === currentEnv,
    );

    const searchedAndFiltered = filtered.filter((log) =>
      Object.values(log).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );

    setFilteredLogs(searchedAndFiltered);
  }, [logs, searchTerm, currentEnv]);

  const handleSort = (key: keyof LogMessage) => {
    const newSortOrder =
      sortKey === key ? (sortOrder === "asc" ? "desc" : "asc") : "asc";
    setSortKey(key);
    setSortOrder(newSortOrder);
  };

  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);

  const nextPage = () => setCurrentPage(currentPage + 1);
  const prevPage = () => setCurrentPage(currentPage - 1);

  return (
    <div style={{ fontFamily: "Arial, sans-serif", padding: "20px" }}>
      <h1 style={{ fontSize: "24px", marginBottom: "20px" }}>Simulator Logs</h1>
      {error && <p className="error">{error}</p>}
      <div style={{ marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Search logs..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            padding: "10px",
            fontSize: "16px",
            width: "100%",
            maxWidth: "400px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />
      </div>
      <div
        style={{ overflowX: "auto", maxWidth: "100%", marginBottom: "20px" }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #ddd",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "5%" }} />
            <col style={{ width: "4%" }} />
            <col style={{ width: "46%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>
          <thead>
            <tr
              style={{
                backgroundColor: "#f2f2f2",
                borderBottom: "1px solid #ddd",
                fontSize: "16px",
                color: "#333",
              }}
            >
              <th style={{ padding: "12px", textAlign: "left" }}>
                Environment{" "}
              </th>
              <th
                style={{ padding: "12px", textAlign: "left" }}
                onClick={() => handleSort("level")}
              >
                Level{" "}
                {sortKey === "level" && sortOrder === "asc" && (
                  <span style={{ fontSize: "14px", color: "black" }}>↑</span>
                )}
                {sortKey === "level" && sortOrder === "desc" && (
                  <span style={{ fontSize: "14px", color: "black" }}>↓</span>
                )}
              </th>
              <th style={{ padding: "12px", textAlign: "left" }}>Message</th>
              <th
                style={{ padding: "12px", textAlign: "left" }}
                onClick={() => handleSort("service")}
              >
                Service{" "}
                {sortKey === "service" && sortOrder === "asc" && (
                  <span style={{ fontSize: "14px", color: "black" }}>↑</span>
                )}
                {sortKey === "service" && sortOrder === "desc" && (
                  <span style={{ fontSize: "14px", color: "black" }}>↓</span>
                )}
              </th>
              <th
                style={{ padding: "12px", textAlign: "left" }}
                onClick={() => handleSort("timestamp")}
              >
                Timestamp{" "}
                {sortKey === "timestamp" && sortOrder === "asc" && (
                  <span style={{ fontSize: "14px", color: "black" }}>↑</span>
                )}
                {sortKey === "timestamp" && sortOrder === "desc" && (
                  <span style={{ fontSize: "14px", color: "black" }}>↓</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {currentLogs.map((log, index) => (
              <tr
                key={index}
                style={{
                  borderBottom: "1px solid #ddd",
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                <td style={{ padding: "12px" }}>{log.env}</td>
                <td style={{ padding: "12px" }}>{log.level}</td>
                <td style={{ padding: "12px", wordWrap: "break-word" }}>
                  {log.message}
                </td>
                <td style={{ padding: "12px" }}>{log.service}</td>
                <td style={{ padding: "12px" }}>{log.timestamp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: "20px", textAlign: "center" }}>
        <button
          onClick={prevPage}
          style={{
            margin: "5px",
            padding: "8px 16px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            background: "#fff",
            color: "#333",
            cursor: "pointer",
          }}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span
          style={{
            margin: "5px",
            padding: "8px 16px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            background: "#007bff",
            color: "#fff",
          }}
        >
          Page {currentPage}
        </span>
        <button
          onClick={nextPage}
          style={{
            margin: "5px",
            padding: "8px 16px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            background: "#fff",
            color: "#333",
            cursor: "pointer",
          }}
          disabled={currentLogs.length < logsPerPage}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Logs;
